/**
 * The 2×2 (and single / MPR / compare) viewport grid plus the toolbar strip.
 *
 * Pane identity matters: each pane id is stable for the life of the layout so
 * that `nvRegistry` can keep the crosshair-link topology in step. Changing a
 * pane's plane must not remount its canvas, or the GL context churns.
 */

import * as React from "react";
import { EmptyState } from "@/components/ui/primitives";
import { cn } from "@/lib/utils";
import { ViewportPane, type PaneOption } from "./ViewportPane";
import { ViewerToolbar } from "./ViewerToolbar";
import { useAssetUrl } from "./useAssetUrl";
import { findNode, nodeAbsPath, useManifest, useViewerStore } from "./viewerStore";
import { LAYOUT_PANE_COUNT, PLANE_LABELS } from "./types";
import type { LayoutMode, ViewPlane, ViewerStatsPayload } from "./types";

const PLANE_OPTIONS: PaneOption[] = (["axial", "sagittal", "coronal", "render"] as ViewPlane[]).map(
  (plane) => ({ value: plane, label: PLANE_LABELS[plane] }),
);

const LAYOUT_CLASS: Record<LayoutMode, string> = {
  single: "grid-cols-1 grid-rows-1",
  "2x2": "grid-cols-2 grid-rows-2",
  mpr: "grid-cols-3 grid-rows-1",
  compare: "grid-cols-2 grid-rows-1",
};

export function ViewportGrid() {
  const manifest = useManifest();
  const store = useViewerStore();
  const { layout, panes } = store;

  const baseNode = findNode(manifest, store.baseNodeId);
  const overlayNode = store.overlayVisible ? findNode(manifest, store.overlayNodeIds[0] ?? null) : null;
  const compareNode = findNode(manifest, store.compareNodeId ?? manifest?.images[1]?.id ?? null);

  const base = useAssetUrl(nodeAbsPath(manifest, baseNode));
  const overlay = useAssetUrl(nodeAbsPath(manifest, overlayNode));
  const compare = useAssetUrl(nodeAbsPath(manifest, compareNode));

  const setLocation = store.setLocation;
  const setStats = store.setStats;
  const onStats = React.useCallback(
    (stats: ViewerStatsPayload) => setStats(stats.base, stats.overlay, stats.masked),
    [setStats],
  );

  const threshold = React.useMemo(
    () => ({ min: store.thresholdMin, max: store.thresholdMax }),
    [store.thresholdMin, store.thresholdMax],
  );

  const visiblePanes = panes.slice(0, LAYOUT_PANE_COUNT[layout]);

  if (!manifest) {
    return (
      <div className="flex h-full flex-col bg-ads-bg">
        <EmptyState
          title="No case open"
          hint="Open or create a case from the Home tab; its images then appear in the Study Explorer."
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-ads-border-strong">
      <div className={cn("grid min-h-0 flex-1 gap-px", LAYOUT_CLASS[layout])}>
        {visiblePanes.map((plane, index) => {
          const isCompareSecond = layout === "compare" && index === 1;
          return (
            <ViewportPane
              key={`pane-${index}`}
              id={`pane-${index}`}
              // Compare shows the same plane in both tiles; the dropdown there
              // picks the volume instead.
              plane={layout === "compare" ? panes[0] : plane}
              title={
                layout === "compare"
                  ? ((isCompareSecond ? compareNode?.label : baseNode?.label) ?? "Volume")
                  : undefined
              }
              baseUrl={isCompareSecond ? compare.url : base.url}
              overlayUrl={overlay.url}
              baseColormap={store.baseColormap}
              overlayColormap={store.overlayColormap}
              overlayOpacity={store.overlayOpacity}
              overlayThreshold={threshold}
              crosshair={store.crosshair}
              linked={store.linked}
              showLabels={store.showLabels}
              showScale={store.showScale}
              headerOptions={
                layout === "compare"
                  ? manifest.images.map((node) => ({ value: node.id, label: node.label }))
                  : PLANE_OPTIONS
              }
              headerValue={
                layout === "compare"
                  ? ((isCompareSecond ? compareNode?.id : baseNode?.id) ?? "")
                  : plane
              }
              onHeaderChange={(value) => {
                if (layout !== "compare") {
                  store.setPanePlane(index, value as ViewPlane);
                } else if (isCompareSecond) {
                  store.setOption("compareNodeId", value);
                } else {
                  store.setBaseNode(value);
                }
              }}
              onLocationChange={setLocation}
              onStats={onStats}
            />
          );
        })}
      </div>

      <ViewerToolbar imageNodes={manifest.images} maskNodes={manifest.masks} />
    </div>
  );
}
